import { CSSProperties, forwardRef, useCallback, useImperativeHandle, useRef } from "react"
import { View } from "react-native"

import { WebViewProps } from "./WebView"

/**
 * Minimal ref surface exposed by the web iframe WebView.
 * Matches the subset of react-native-webview methods used in this codebase.
 */
interface WebViewWebHandle {
  goBack: () => void
  goForward: () => void
  reload: () => void
  stopLoading: () => void
  injectJavaScript: (script: string) => void
}

export const WebView = forwardRef<WebViewWebHandle, WebViewProps>(function WebView(
  props: WebViewProps,
  ref,
) {
  const {
    containerStyle,
    source,
    style,
    webStyle,
    onEmbeddingError,
    onLoad,
    onError,
  } = props

  const iframeRef = useRef<HTMLIFrameElement>(null)

  useImperativeHandle(ref, () => ({
    // history.back() / history.forward() require accessing `contentWindow.history`,
    // which the browser blocks with a SecurityError for cross-origin frames.
    // Back/forward navigation in an embedded cross-origin iframe is not possible
    // from the parent frame; canGoBack is always false on web (see handleLoad).
    goBack: () => {},
    goForward: () => {},
    reload: () => {
      try {
        if (iframeRef.current) {
          iframeRef.current.src = iframeRef.current.src
        }
      } catch {}
    },
    stopLoading: () => {},
    injectJavaScript: () => {},
  }))

  // Detect iframe embedding failures (X-Frame-Options / CSP frame-ancestors).
  //
  // Cross-origin embedding failure is NOT reliably detectable from the parent frame:
  // the `load` event fires for both success and failure, and once cross-origin content
  // (or a browser error page) is in the frame, the same-origin policy throws SecurityError
  // on every property — empirically a successfully-loaded page (example.com) and a blocked
  // page (google.com) are byte-for-byte identical: load fires, length===0, href/document
  // throw SecurityError. So there is no DOM signal to discriminate them in Chrome.
  //
  // The one case we CAN catch is Firefox/Safari, which reset a blocked frame to about:blank
  // (same-origin, so readable). Reliable cross-browser detection requires a backend
  // header pre-check (see callers).
  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      // Always forward load so callers can clear their loading state.
      onLoad?.(e as any)

      // canGoBack/canGoForward are not reportable for cross-origin iframes:
      // the browser blocks all access to `contentWindow.history` with a
      // SecurityError, so neither reading the history length nor calling
      // history.back() is possible. We therefore never report canGoBack=true
      // on web, keeping the navigation buttons disabled (which is honest — they
      // can't do anything). Native (react-native-webview) handles this correctly.

      // Embedding-error detection (Firefox/Safari can detect about:blank resets).
      if (!onEmbeddingError) return
      const iframe = e.currentTarget
      try {
        const href = iframe.contentWindow?.location.href ?? ""
        if (href === "about:blank" || href === "") {
          onEmbeddingError()
          return
        }
        const bodyContent = iframe.contentDocument?.body?.innerHTML?.trim() ?? ""
        if (bodyContent === "") {
          onEmbeddingError()
        }
      } catch {
        // SecurityError → cross-origin content loaded (success) or Chrome's error page.
        // Indistinguishable and not actionable here, so do nothing.
      }
    },
    [onLoad, onEmbeddingError],
  )

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      onError?.(e as any)
    },
    [onError],
  )

  if (!!source && "uri" in source) {
    return (
      // Honor the standard RN `style` prop (callers pass `style`, not `containerStyle`).
      // Without it the container View collapses to 0 height and the absolutely-positioned
      // iframe renders blank (white screen).
      <View style={[style, containerStyle]}>
        <iframe
          ref={iframeRef}
          src={source.uri}
          style={{ ...$iframe, ...webStyle }}
          onLoad={handleLoad}
          onError={handleError}
        />
      </View>
    )
  }

  return null
})

const $iframe: CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  border: 0,
}
